/**
 * QUIL: pasted custom themes, per device (app-platform theme contract section 5, task 3.6).
 *
 * A custom theme is a `theme1:` code (app-platform `themeCode.ts`), validated against quil's profile
 * on paste by `customThemeAdd.ts` (lazy: the engine never reaches the eager bundle). What is stored
 * here is the result: the code, its name and scheme, and the derived `--theme-*` map, so applying
 * one at boot needs no engine. Per device, by decision: localStorage, never Dexie or OneDrive.
 *
 *   quil.theme.custom   StoredCustomTheme[]      (the hub's `customThemesKey('quil')`)
 *   quil.theme.active   the active custom id, or absent for the built-in Theme setting
 *
 * Every read is defensive: a malformed list or entry is dropped, and a var that is not `--theme-*`
 * or not a plain colour literal never reaches the page. Storage that throws reads as empty.
 */

import { customThemesKey } from './themeMode'

export const QUIL_THEME_KEY = 'quil'
const LIST_KEY = customThemesKey(QUIL_THEME_KEY)
const ACTIVE_KEY = `${QUIL_THEME_KEY}.theme.active`
/** Fired on window when the list or the active custom theme changes, so App can re-apply. */
export const CUSTOM_THEME_EVENT = 'quil:custom-theme'

export interface StoredCustomTheme {
  /** `themeCodeId(theme)`: stable for the same code on any device. */
  id: string
  name: string
  code: string
  scheme: 'light' | 'dark' | 'oled'
  bg: string
  accent: string
  /** The derived role map, `--theme-*` -> colour literal. */
  vars: Record<string, string>
}

const VAR = /^--theme-[a-z0-9-]+$/
const SAFE = /^(#[0-9a-fA-F]{3,8}|(rgb|rgba|hsl|hsla)\([0-9.,%\s/+-]*\))$/
const ID = /^[A-Za-z0-9_-]{1,64}$/

/** localStorage, or null where touching it throws. The one accessor for quil's theme keys. */
export function themeStorage(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

function clean(x: unknown): StoredCustomTheme | null {
  if (typeof x !== 'object' || x === null) return null
  const o = x as Record<string, unknown>
  if (typeof o.id !== 'string' || !ID.test(o.id)) return null
  if (typeof o.name !== 'string' || typeof o.code !== 'string') return null
  if (o.scheme !== 'light' && o.scheme !== 'dark' && o.scheme !== 'oled') return null
  if (typeof o.bg !== 'string' || !SAFE.test(o.bg) || typeof o.accent !== 'string' || !SAFE.test(o.accent)) return null
  if (typeof o.vars !== 'object' || o.vars === null) return null
  const vars: Record<string, string> = {}
  for (const [k, v] of Object.entries(o.vars as Record<string, unknown>)) {
    if (VAR.test(k) && typeof v === 'string' && SAFE.test(v.trim())) vars[k] = v.trim()
  }
  return { id: o.id, name: o.name, code: o.code, scheme: o.scheme, bg: o.bg, accent: o.accent, vars }
}

export function readCustomThemes(): StoredCustomTheme[] {
  try {
    const raw = themeStorage()?.getItem(LIST_KEY)
    if (!raw) return []
    const list: unknown = JSON.parse(raw)
    if (!Array.isArray(list)) return []
    return list.map(clean).filter((t): t is StoredCustomTheme => t !== null)
  } catch {
    return []
  }
}

function changed(): void {
  try {
    window.dispatchEvent(new Event(CUSTOM_THEME_EVENT))
  } catch {
    /* no window: nothing to tell */
  }
}

/** Replace the stored list. Returns false when storage is blocked or full. */
export function saveCustomThemes(list: StoredCustomTheme[]): boolean {
  try {
    themeStorage()?.setItem(LIST_KEY, JSON.stringify(list))
    changed()
    return true
  } catch {
    return false
  }
}

export function getActiveCustomId(): string | null {
  try {
    const id = themeStorage()?.getItem(ACTIVE_KEY) ?? null
    return id !== null && ID.test(id) ? id : null
  } catch {
    return null
  }
}

/** Make a custom theme active on this device, or `null` to go back to the built-in Theme setting. */
export function setActiveCustomId(id: string | null): void {
  try {
    if (id === null) themeStorage()?.removeItem(ACTIVE_KEY)
    else themeStorage()?.setItem(ACTIVE_KEY, id)
  } catch {
    /* blocked storage: the change simply does not stick */
  }
  changed()
}

/** The active custom theme, when one is set and still stored. */
export function activeCustomTheme(): StoredCustomTheme | null {
  const id = getActiveCustomId()
  if (id === null) return null
  return readCustomThemes().find((t) => t.id === id) ?? null
}

/** Remove one; if it was active, the built-in Theme setting takes over again. */
export function removeCustomTheme(id: string): void {
  saveCustomThemes(readCustomThemes().filter((t) => t.id !== id))
  if (getActiveCustomId() === id) setActiveCustomId(null)
}
